import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  finalize,
  firstValueFrom,
  map,
  of,
  throwError
} from 'rxjs';
import { environment } from '../../environments/environment';
import { Role } from '../enums/roles.enum';
import {
  AccessProfile,
  AuthLoginResponse,
  AuthRefreshResponse,
  FeatureFlags,
  NavigationItemDto,
  UserDetailsDto
} from '../interfaces/auth.model';
import { UserClientRegistration, UserRegistration } from '../interfaces/register.model';
import { ApiResponse, PaginatedResponse } from '../interfaces/shared.model';
import { parseJwt } from '../utils/jwt.util';
import { NAV_TRANSLATIONS } from '../i18n/nav-translations';
import { iconSubset } from '../icons/icon-subset';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly knownIconNames = new Set(Object.keys(iconSubset));
  private readonly authUrl = `${environment.apiUrl}/auth`;
  private readonly accessUrl = `${environment.apiUrl}/access`;
  private readonly profileImageBaseUrl = environment.profileImageBaseUrl ?? '';

  private readonly accessTokenKey = environment.auth?.accessTokenKey ?? 'token';
  private readonly refreshTokenKey = environment.auth?.refreshTokenKey ?? 'refresh_token';
  private readonly profileKey = environment.auth?.profileKey ?? 'auth_profile';
  private readonly profileEtagKey = environment.auth?.profileEtagKey ?? 'auth_profile_etag';
  private readonly navEtagKey = environment.auth?.navEtagKey ?? 'nav_etag';
  private readonly featuresEtagKey = environment.auth?.featuresEtagKey ?? 'features_etag';
  private readonly permissionsVersionKey = environment.auth?.permissionsVersionKey ?? 'permissions_version';

  private refreshPromise: Promise<string | null> | null = null;
  private profileEtag: string | null = localStorage.getItem(this.profileEtagKey);
  private navEtag: string | null = localStorage.getItem(this.navEtagKey);
  private featuresEtag: string | null = localStorage.getItem(this.featuresEtagKey);

  private profileSubject = new BehaviorSubject<AccessProfile | null>(this.restoreProfile());
  readonly profile$ = this.profileSubject.asObservable();

  constructor(private http: HttpClient) {}

  get currentProfile(): AccessProfile | null {
    return this.profileSubject.value;
  }

  login(username: string, password: string): Observable<AccessProfile> {
    return this.http
      .post<AuthLoginResponse>(`${this.authUrl}/login`, { username, password }, { observe: 'response' })
      .pipe(
        map(response => this.handleAuthResponse(response)),
        catchError(error => {
          this.clearSession();
          return throwError(() => error);
        })
      );
  }

  logout(options: { revoke?: boolean } = { revoke: true }): void {
    const refreshToken = this.getRefreshToken();
    this.clearSession();
    if (options.revoke && refreshToken) {
      this.http.post(`${this.authUrl}/logout`, { refreshToken }).subscribe({
        // Best-effort revoke; user is already signed out on the client
        error: () => {},
      });
    }
  }

  async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return null;
    }

    const request$ = this.http
      .post<AuthRefreshResponse>(`${this.authUrl}/refresh`, { refreshToken }, { observe: 'response' })
      .pipe(
        map(response => this.handleRefreshResponse(response)),
        catchError(() => {
          this.clearSession();
          return of(null);
        }),
        finalize(() => {
          this.refreshPromise = null;
        })
      );

    this.refreshPromise = firstValueFrom(request$);
    return this.refreshPromise;
  }

  async fetchAccessProfile(force = false): Promise<AccessProfile | null> {
    const headers = this.buildConditionalHeaders(force ? null : this.profileEtag);

    return firstValueFrom(
      this.http.get<AccessProfile>(`${this.authUrl}/me`, { headers, observe: 'response' }).pipe(
        map(response => {
          if (response.status === 304) {
            return this.currentProfile;
          }

          const payload = this.unwrap<AccessProfile>(response.body);
          if (payload) {
            this.setProfile(payload, response.headers.get('ETag'));
          }
          return this.currentProfile;
        }),
        catchError(error => {
          if (error.status === 304) {
            return of(this.currentProfile);
          }
          if (error.status === 401) {
            this.clearSession();
          }
          return of(null);
        })
      )
    );
  }

  async fetchNav(force = false): Promise<NavigationItemDto[] | undefined> {
    const headers = this.buildConditionalHeaders(force ? null : this.navEtag);
    return firstValueFrom(
      this.http
        .get<any>(`${this.accessUrl}/nav`, { headers, observe: 'response' })
        .pipe(
          map(response => {
            if (response.status === 304) {
              return this.currentProfile?.nav;
            }

            const rawPayload = this.unwrap<any>(response.body as any);
            const { nav, permissionsVersion, updatedAt } = this.mapNavPayload(rawPayload);
            if (nav.length > 0) {
              this.navEtag = response.headers.get('ETag');
              if (this.navEtag) {
                localStorage.setItem(this.navEtagKey, this.navEtag);
              }

              this.mergeProfile(
                {
                  nav,
                  permissionsVersion,
                  updatedAt,
                },
                this.profileEtag
              );
            }

            return this.currentProfile?.nav;
          }),
          catchError(error => {
            if (error.status === 304) {
              return of(this.currentProfile?.nav);
            }
            return of(this.currentProfile?.nav);
          })
        )
    );
  }

  async fetchFeatures(force = false): Promise<FeatureFlags | undefined> {
    const headers = this.buildConditionalHeaders(force ? null : this.featuresEtag);
    return firstValueFrom(
      this.http
        .get<any>(`${this.accessUrl}/features`, { headers, observe: 'response' })
        .pipe(
          map(response => {
            if (response.status === 304) {
              return this.currentProfile?.features;
            }

            const rawPayload = this.unwrap<any>(response.body as any);
            const { features, permissionsVersion, updatedAt } = this.mapFeaturesPayload(rawPayload);
            if (features) {
              this.featuresEtag = response.headers.get('ETag');
              if (this.featuresEtag) {
                localStorage.setItem(this.featuresEtagKey, this.featuresEtag);
              }

              this.mergeProfile(
                {
                  features,
                  permissionsVersion,
                  updatedAt,
                },
                this.profileEtag
              );
            }

            return this.currentProfile?.features;
          }),
          catchError(error => {
            if (error.status === 304) {
              return of(this.currentProfile?.features);
            }
            return of(this.currentProfile?.features);
          })
        )
    );
  }

  getAccessToken(): string | null {
    const token = localStorage.getItem(this.accessTokenKey);
    if (!token) return null;

    if (this.isTokenExpired(token)) {
      return null;
    }

    return token;
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  isTokenExpired(token: string | null = null): boolean {
    const tokenToCheck = token ?? localStorage.getItem(this.accessTokenKey);
    if (!tokenToCheck) {
      return true;
    }

    const decoded = this.decodeJwtPayload(tokenToCheck);
    if (!decoded) {
      return true;
    }

    const exp = decoded.exp;
    if (exp === undefined || exp === null) {
      return false;
    }

    const expiry = typeof exp === 'number' ? exp : parseInt(exp, 10);
    if (Number.isNaN(expiry)) {
      return true;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return expiry <= nowInSeconds;
  }

  getDecodedToken<T = any>(token?: string | null): T | null {
    const tokenToDecode = token ?? this.getAccessToken();
    if (!tokenToDecode) return null;

    return this.decodeJwtPayload(tokenToDecode) as T;
  }

  getToken(): string | null {
    return this.getAccessToken();
  }

  getPermissionsVersion(): string | undefined {
    const decoded = this.getDecodedToken<any>();
    return (
      this.currentProfile?.permissionsVersion ??
      localStorage.getItem(this.permissionsVersionKey) ??
      decoded?.permsVersion ??
      decoded?.permissionsVersion
    );
  }

  hasPermission(permission: string): boolean {
    const granted = this.getPermissions();
    if (this.hasWildcard(granted)) {
      return true;
    }
    return granted.includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    const granted = this.getPermissions();
    if (this.hasWildcard(granted)) {
      return true;
    }
    return permissions.some(p => granted.includes(p));
  }

  hasRole(role: string): boolean {
    return this.getUserRoles().includes(role);
  }

  getPermissions(): string[] {
    const fromProfile = this.currentProfile?.permissions;
    if (fromProfile?.length) return fromProfile;

    const decoded = this.getDecodedToken<any>();
    if (!decoded) return [];

    return this.normalizePermissions(decoded.permissions);
  }

  getUserRoles(): string[] {
    const fromProfile = this.currentProfile?.roles;
    if (fromProfile?.length) return fromProfile;

    const decoded = this.getDecodedToken<any>();
    return this.extractRoles(decoded);
  }

  getUserSub(): string | null {
    const token = this.getAccessToken();
    if (!token) return null;

    const decoded = this.getDecodedToken<any>(token);
    return decoded?.sub || null;
  }

  getUserName(): string | null {
    const fromProfile =
      this.currentProfile?.user?.name ||
      this.currentProfile?.user?.fullName ||
      this.currentProfile?.user?.userName;
    if (fromProfile) return fromProfile;

    const decoded = this.getDecodedToken<any>();
    return decoded?.name || decoded?.unique_name || null;
  }

  getProfilePictureUrl(): string | null {
    return this.resolveProfilePictureUrl(this.currentProfile?.user?.profilePictureUrl ?? null);
  }

  getUserLanguage(): string | null {
    const fromProfile = this.normalizeLanguage(this.currentProfile?.user?.language ?? this.currentProfile?.user?.locale);
    if (fromProfile) {
      return fromProfile;
    }

    const decoded = this.getDecodedToken<any>();
    return this.normalizeLanguage(
      decoded?.language ?? decoded?.lang ?? decoded?.locale ?? decoded?.ui_locale ?? decoded?.uiLocale
    );
  }

  getUserTimezone(): string | null {
    const fromProfile = this.normalizeLanguage(
      this.currentProfile?.user?.['timezone'] ?? this.currentProfile?.user?.['timeZone']
    );
    if (fromProfile) {
      return fromProfile;
    }

    const decoded = this.getDecodedToken<any>();
    return this.normalizeLanguage(decoded?.timezone ?? decoded?.timeZone ?? decoded?.tz);
  }

  getBranchId(): number | null {
    const token = this.getAccessToken();
    if (!token) return null;

    const decoded = this.getDecodedToken<any>(token);
    const value = decoded?.branchId ?? decoded?.BranchId;
    return value !== undefined && value !== null ? Number(value) : null;
  }

  getAgentCode(): string | number | null {
    const token = this.getAccessToken();
    if (!token) return null;

    const decoded = this.getDecodedToken<any>(token);
    return decoded?.AgentCode ?? decoded?.agentCode ?? null;
  }

  getAgentCodes(): string[] {
    const token = this.getAccessToken();
    if (!token) return [];

    const decoded = this.getDecodedToken<any>(token);
    if (!decoded || !decoded.AgentCode) return [];

    let agentCodes = decoded.AgentCode;

    const roles = this.extractRoles(decoded);
    if (roles.includes('Manager') || roles.includes(Role.Admin)) {
      agentCodes = '3072,4894,5408,1610';
    }

    if (typeof agentCodes === 'string') {
      return agentCodes.split(',').map(code => `leader-${code.trim()}`);
    }

    return [];
  }

  private hasWildcard(granted: string[]): boolean {
    return granted.includes('*') || granted.includes('*:*');
  }

  getNavItems(): NavigationItemDto[] {
    return this.currentProfile?.nav ?? [];
  }

  getFeatures(): FeatureFlags {
    return this.currentProfile?.features ?? {};
  }

  isFeatureEnabled(flag: string): boolean {
    return !!this.getFeatures()?.[flag];
  }

  ensureProfileCached(): Promise<AccessProfile | null> {
    if (this.currentProfile) {
      return Promise.resolve(this.currentProfile);
    }
    return this.fetchAccessProfile();
  }

  registerUser(user: UserRegistration): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.authUrl}/register`, user);
  }

  registerClientUser(user: UserClientRegistration): Observable<any> {
    return this.http.post<any>(`${this.authUrl}/client-register`, user);
  }

  uploadMyProfilePicture(file: File): Observable<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<string>>(`${this.authUrl}/me/profile-picture`, formData);
  }

  uploadUserProfilePicture(userId: string | number, file: File): Observable<ApiResponse<string>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponse<string>>(`${this.authUrl}/users/${userId}/profile-picture`, formData);
  }

  getUsers(
    search = '',
    isEmployee?: boolean,
    pageNumber = 1,
    pageSize = 20,
    branchId = 0
  ): Observable<PaginatedResponse<UserSummary>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);

    if (search.trim()) params = params.set('search', search.trim());
    if (isEmployee !== undefined) params = params.set('isEmployee', isEmployee);
    if (branchId > 0) params = params.set('branchId', branchId);

    return this.http
      .get<ApiResponse<PaginatedResponse<UserSummary>>>(this.authUrl, { params })
      .pipe(
        map(resp => {
          if (!resp.success) throw new Error(resp.message);
          return resp.data;
        })
      );
  }

  getUserDetails(userId: string | number): Observable<UserDetailsDto> {
    const normalizedUserId = encodeURIComponent(String(userId));
    return this.http
      .get<ApiResponse<UserDetailsDto> | UserDetailsDto>(`${this.authUrl}/user-details/${normalizedUserId}`)
      .pipe(
        map(resp => {
          const details = this.unwrap<UserDetailsDto>(resp);
          if (!details) {
            throw new Error('Empty user details response');
          }
          return {
            ...details,
            profilePictureUrl: this.resolveProfilePictureUrl(details.profilePictureUrl ?? null)
          };
        })
      );
  }

  changePassword(request: ChangePasswordRequest): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.authUrl}/change-password`, request);
  }

  adminResetPassword(request: AdminResetPasswordRequest): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.authUrl}/admin/reset-password`, request);
  }

  private handleAuthResponse(
    response: HttpResponse<AuthLoginResponse | AccessProfile | ApiResponse<AuthLoginResponse | AccessProfile>>
  ): AccessProfile {
    const body = this.unwrap<AuthLoginResponse | AccessProfile>(response.body);
    if (!body) {
      throw new Error('Empty auth response');
    }

    const authBody = body as AuthLoginResponse;
    if (authBody.accessToken) {
      this.storeTokens(authBody.accessToken, authBody.refreshToken);
    }

    this.setProfile(body, response.headers.get('ETag'));
    return this.currentProfile!;
  }

  private handleRefreshResponse(
    response: HttpResponse<AuthRefreshResponse | ApiResponse<AuthRefreshResponse>>
  ): string | null {
    const body = this.unwrap<AuthRefreshResponse>(response.body as any);
    if (!body || !body.accessToken) {
      this.clearSession();
      return null;
    }

    this.storeTokens(body.accessToken, body.refreshToken);
    this.mergeProfile(
      {
        roles: body.roles,
        permissions: this.normalizePermissions(body.permissions as any),
        permissionsVersion: body.permissionsVersion,
        updatedAt: body.updatedAt,
      },
      response.headers.get('ETag')
    );

    return body.accessToken;
  }

  private setProfile(profile: AccessProfile, etag?: string | null): void {
    const normalizedNav = this.mapNavItems(profile.nav as any);
    const normalized: AccessProfile = {
      user: profile.user,
      roles: profile.roles ?? [],
      permissions: this.normalizePermissions(profile.permissions as any),
      nav: normalizedNav,
      features: profile.features ?? {},
      permissionsVersion: profile.permissionsVersion,
      updatedAt: profile.updatedAt,
    };

    this.profileSubject.next(normalized);
    this.persistProfile(normalized, etag ?? this.profileEtag);
  }

  private mergeProfile(update: Partial<AccessProfile>, etag?: string | null): void {
    const current = this.currentProfile;
    if (!current) {
      return;
    }

    const merged: AccessProfile = {
      ...current,
      ...update,
      roles: update.roles ?? current.roles ?? [],
      permissions: this.normalizePermissions(update.permissions ?? current.permissions),
      nav: update.nav ? this.mapNavItems(update.nav as any) : current.nav,
      features: update.features ?? current.features,
      permissionsVersion: update.permissionsVersion ?? current.permissionsVersion,
      updatedAt: update.updatedAt ?? current.updatedAt,
    };

    this.setProfile(merged, etag ?? this.profileEtag);
  }

  private persistProfile(profile: AccessProfile, etag?: string | null): void {
    localStorage.setItem(this.profileKey, JSON.stringify(profile));
    if (profile.permissionsVersion) {
      localStorage.setItem(this.permissionsVersionKey, profile.permissionsVersion);
    } else {
      localStorage.removeItem(this.permissionsVersionKey);
    }

    if (etag) {
      this.profileEtag = etag;
      localStorage.setItem(this.profileEtagKey, etag);
    }
  }

  private buildConditionalHeaders(etag: string | null): HttpHeaders {
    let headers = new HttpHeaders();
    if (etag) {
      headers = headers.set('If-None-Match', etag);
    }
    return headers;
  }

  private storeTokens(accessToken: string, refreshToken?: string): void {
    localStorage.setItem(this.accessTokenKey, accessToken);
    localStorage.setItem('token', accessToken); // legacy key for existing consumers
    if (refreshToken) {
      localStorage.setItem(this.refreshTokenKey, refreshToken);
    }
  }

  private clearSession(): void {
    localStorage.removeItem(this.accessTokenKey);
    localStorage.removeItem('token');
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.profileKey);
    localStorage.removeItem(this.profileEtagKey);
    localStorage.removeItem(this.navEtagKey);
    localStorage.removeItem(this.featuresEtagKey);
    localStorage.removeItem(this.permissionsVersionKey);

    this.profileEtag = null;
    this.navEtag = null;
    this.featuresEtag = null;
    this.refreshPromise = null;
    this.profileSubject.next(null);
  }

  private decodeJwtPayload(token: string): any {
    try {
      return parseJwt(token);
    } catch (error) {
      console.error('Error decoding JWT:', error);
      return null;
    }
  }

  private restoreProfile(): AccessProfile | null {
    const stored = localStorage.getItem(this.profileKey);
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored) as AccessProfile;
      return {
        ...parsed,
        roles: parsed.roles ?? [],
        permissions: this.normalizePermissions(parsed.permissions as any),
        nav: this.mapNavItems(parsed.nav as any),
        features: parsed.features ?? {},
      };
    } catch {
      return null;
    }
  }

  private unwrap<T>(body: T | ApiResponse<T> | null | undefined): T | null {
    if (!body) return null;
    const maybeResponse = body as ApiResponse<T>;
    if (maybeResponse && typeof maybeResponse === 'object' && 'data' in maybeResponse) {
      return (maybeResponse as ApiResponse<T>).data ?? null;
    }
    return body as T;
  }

  private mapNavPayload(payload: any): {
    nav: NavigationItemDto[];
    permissionsVersion?: string;
    updatedAt?: string;
  } {
    const rawNav = payload?.nav ?? payload?.items ?? payload;
    return {
      nav: this.mapNavItems(rawNav),
      permissionsVersion: payload?.permissionsVersion ?? payload?.version,
      updatedAt: payload?.updatedAt ?? payload?.updatedAtUtc,
    };
  }

  private mapFeaturesPayload(payload: any): {
    features?: FeatureFlags;
    permissionsVersion?: string;
    updatedAt?: string;
  } {
    return {
      features: payload?.features ?? payload?.flags,
      permissionsVersion: payload?.permissionsVersion ?? payload?.version,
      updatedAt: payload?.updatedAt ?? payload?.updatedAtUtc,
    };
  }

  private mapNavItems(rawNav: any): NavigationItemDto[] {
    if (!rawNav || !Array.isArray(rawNav)) {
      return [];
    }

    const mapItem = (item: any): NavigationItemDto | null => {
      if (!item) return null;

      const permissions = this.normalizePermissions(item.permissions ?? item.requires);
      const children = this.mapNavItems(item.children ?? item.items);
      const i18nKey = item.i18nKey ?? item.key;
      const name = item.name ?? item.label ?? item.title ?? i18nKey;
      const url = item.url ?? item.path ?? item.href ?? item.to;
      const featureFlag = item.featureFlag ?? item.feature ?? item.flag;
      const roles = item.roles ?? item.requiredRoles ?? item.allowedRoles;
      const rawIconComponent = item.iconComponent;
      const normalizedIconComponent =
        rawIconComponent && typeof rawIconComponent === 'object'
          ? { ...rawIconComponent }
          : rawIconComponent
            ? { name: rawIconComponent }
            : undefined;
      const iconClassRaw = item.icon ?? item.iconName;
      const iconClass =
        typeof iconClassRaw === 'string' && iconClassRaw.trim().length === 0
          ? undefined
          : iconClassRaw;
      const iconClassValue = typeof iconClass === 'string' ? iconClass.trim() : undefined;
      const iconNameFromComponent = this.normalizeKnownIconName(normalizedIconComponent?.name);
      const iconNameFromClass = this.normalizeKnownIconName(iconClassValue);
      const fallbackIconName =
        this.getFallbackNavIcon(i18nKey) ??
        this.getFallbackNavIcon(name) ??
        this.getFallbackNavIcon(item.key) ??
        this.getFallbackNavIcon(item.label) ??
        this.getFallbackNavIcon(item.title) ??
        this.getFallbackNavIcon(url);
      const hasProvidedContent = !!normalizedIconComponent?.content;
      const hasCssClassIcon = !!iconClassValue && !iconNameFromClass;
      let resolvedIconName = iconNameFromComponent ?? iconNameFromClass;

      if (!resolvedIconName && !hasProvidedContent) {
        if (fallbackIconName) {
          resolvedIconName = fallbackIconName;
        } else if (!hasCssClassIcon) {
          resolvedIconName = this.getDefaultNavIcon();
        }
      }

      const iconComponent = hasProvidedContent || resolvedIconName
        ? {
            ...normalizedIconComponent,
            ...(resolvedIconName ? { name: resolvedIconName } : {})
          }
        : undefined;

      const navItem: NavigationItemDto = {
        name,
        i18nKey,
        url,
        icon: hasCssClassIcon && !resolvedIconName && !hasProvidedContent ? iconClassValue : undefined,
        iconComponent: iconComponent as any,
        roles,
        permissions,
        featureFlag,
        children: children.length > 0 ? children : undefined,
      };

      if (!navItem.name && !navItem.children) {
        return null;
      }

      return this.translateNavItem(navItem);
    };

    return rawNav
      .map(mapItem)
      .filter((i): i is NavigationItemDto => !!i);
  }

  private translateNavItem(navItem: NavigationItemDto): NavigationItemDto {
    const translated =
      (navItem.i18nKey && NAV_TRANSLATIONS[navItem.i18nKey]) ??
      (navItem.name && NAV_TRANSLATIONS[navItem.name]);

    if (translated) {
      return {
        ...navItem,
        name: translated,
      };
    }

    if (!navItem.name && navItem.i18nKey) {
      return {
        ...navItem,
        name: navItem.i18nKey,
      };
    }

    return navItem;
  }

  private getFallbackNavIcon(value?: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (!normalized) {
      return null;
    }

    if (normalized.includes('memorial-plaques') || normalized.includes('/memorial-plaques')) {
      return 'cilBookmark';
    }

    if (normalized.includes('leads') || normalized.includes('/leads')) {
      return 'cilUserFollow';
    }

    switch (normalized) {
      case 'nav.accounting':
      case 'contabilidad':
        return 'cilCalculator';
      case 'nav.accounting.reimbursements':
      case 'nav.accounting.reimbursement':
      case 'reembolso':
      case 'reembolsos':
        return 'cilDollar';
      case 'nav.memorialplaques':
      case 'placas conmemorativas':
      case 'placa conmemorativa':
      case 'memorial plaques':
      case 'memorial plaque':
        return 'cilBookmark';
      case 'nav.leads':
      case 'leads':
      case 'prospectos':
      case 'prospecto':
        return 'cilUserFollow';
      case 'nav.logistics':
      case 'logistica':
      case 'logistics':
        return 'cilTruck';
      case 'nav.training':
      case 'capacitaciones':
      case 'training':
        return 'cilEducation';
      case 'nav.logistics.digitaldisplays':
      case 'pantallas digitales':
        return 'cilMonitor';
      case 'nav.logistics.digitaldisplays.manage':
      case 'gestion de pantallas':
        return 'cilSettings';
      case 'nav.logistics.digitaldisplays.mine':
      case 'nav.servicesmonitor':
      case 'monitor de servicios':
      case 'events/services-monitor':
      case '/events/services-monitor':
        return 'cilSpeedometer';
      case 'nav.myscreens':
      case 'mis pantallas':
      case 'events/my-screens':
      case '/events/my-screens':
        return 'cilGrid';
      default:
        return null;
    }
  }

  private getDefaultNavIcon(): string {
    return 'cilList';
  }

  private normalizeKnownIconName(iconName: unknown): string | undefined {
    if (typeof iconName !== 'string') {
      return undefined;
    }

    const trimmed = iconName.trim();
    if (!trimmed) {
      return undefined;
    }

    const normalized = this.normalizeIconToken(trimmed);
    return this.knownIconNames.has(normalized) ? normalized : undefined;
  }

  private normalizeIconToken(iconName: string): string {
    if (iconName.startsWith('cil-') || iconName.startsWith('cib-')) {
      return iconName.replace(/-([a-z0-9])/g, (_, char: string) => char.toUpperCase());
    }

    return iconName;
  }

  private normalizeLanguage(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  private normalizePermissions(raw: any): string[] {
    if (!raw) return [];

    if (Array.isArray(raw)) {
      return raw
        .map(permission => {
          if (!permission) return null;
          if (typeof permission === 'string') {
            return permission.trim();
          }
          const resource = permission.resource ?? permission.resourceName ?? permission.key ?? permission.id;
          const action = permission.action ?? permission.permission ?? permission.name;
          if (resource && action) {
            return `${resource}:${action}`;
          }
          return null;
        })
        .filter((p): p is string => !!p);
    }

    if (typeof raw === 'string') {
      return raw
        .split(',')
        .map((p: string) => p.trim())
        .filter(Boolean);
    }

    return [];
  }

  private extractRoles(decoded: any): string[] {
    if (!decoded) return [];
    const rawRoles =
      decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ??
      decoded.role ??
      decoded.roles;
    if (!rawRoles) return [];
    return Array.isArray(rawRoles) ? rawRoles : [rawRoles];
  }

  private resolveProfilePictureUrl(path: string | null | undefined): string | null {
    if (!path) {
      return null;
    }
    if (this.isAbsoluteUrl(path)) {
      return path;
    }
    if (!this.profileImageBaseUrl) {
      return path;
    }
    return this.joinUrl(this.profileImageBaseUrl, path);
  }

  private isAbsoluteUrl(value: string): boolean {
    return /^https?:\/\//i.test(value) || value.startsWith('data:');
  }

  private joinUrl(base: string, path: string): string {
    const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    return `${normalizedBase}/${normalizedPath}`;
  }
}

export interface UserSummary {
  id: string;
  userName: string;
  email: string;
  emailConfirmed: boolean;
  isEmployee: boolean;
  lockoutEnd: string | null;
  roles: string[];
  profilePictureUrl?: string | null;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface AdminResetPasswordRequest {
  userId: string;
  newPassword: string;
}
